import React, { useState, useEffect } from 'react';
import { Plus, X, Settings, Eye, EyeOff, Grid, ChevronDown, Move, Edit3, HomeIcon } from 'lucide-react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import UpcomingEventsWidget from './UpcomingEventsWidget';
import RecentEntriesWidget from './RecentEntriesWidget';
import YearToDateStatsWidget from './YearToDateStatsWidget';
import VeterinarianWidget from './VeterinarianWidget';
import FarrierWidget from './FarrierWidget';
import MembershipWidget from './MembershipWidget';
import { getApiUrl } from "../../config/api";

// Widget registry - defines all available widgets
const WIDGET_REGISTRY = {
  upcomingEvents: {
    id: 'upcomingEvents',
    name: 'Reminders',
    component: UpcomingEventsWidget,
    defaultConfig: {
      title: 'Reminders',
      maxEvents: 5,
      daysAhead: 365, // Changed from 30 to 365 to show events up to a year ahead
      showLocation: true,
      showTime: true
    },
    description: 'Shows your upcoming calendar events and reminders',
    icon: 'Calendar',
    category: 'calendar'
  },
  recentEntries: {
    id: 'recentEntries',
    name: 'Recent Entries',
    component: RecentEntriesWidget,
    defaultConfig: {
      title: 'Recent Entries',
      maxEntries: 5
    },
    description: 'Shows your most recent competition entries',
    icon: 'FileText',
    category: 'entries'
  },
  yearToDateStats: {
    id: 'yearToDateStats',
    name: 'Year to Date Stats',
    component: YearToDateStatsWidget,
    defaultConfig: {
      title: 'YTD Results Stats',
      showPoints: true,
      showEarnings: true,
      startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      showDateRange: true,
      showTrend: true
    },
    description: 'Shows total points and earnings from results for a configurable date range',
    icon: 'TrendingUp',
    category: 'statistics'
  },
  veterinarian: {
    id: 'veterinarian',
    name: 'Primary Veterinarian',
    component: VeterinarianWidget,
    defaultConfig: {
      title: 'Primary Veterinarian',
      showClinic: true,
      showEmail: false,
      selectedVetId: null
    },
    description: 'Quick access to your primary veterinarian with contact information',
    icon: 'User',
    category: 'health'
  },
  farrier: {
    id: 'farrier',
    name: 'Primary Farrier',
    component: FarrierWidget,
    defaultConfig: {
      title: 'Primary Farrier',
      showClinic: true,
      showEmail: false,
      selectedFarrierId: null
    },
    description: 'Quick access to your primary farrier with contact information',
    icon: 'Footprints',
    category: 'health'
  },
  membership: {
    id: 'membership',
    name: 'Memberships',
    component: MembershipWidget,
    defaultConfig: {
      title: 'Memberships',
      maxMemberships: 5,
      showExpiringSoon: true,
      showExpired: false,
      showActive: true
    },
    description: 'Track your active association memberships and expiration dates',
    icon: 'Users',
    category: 'associations'
  }
};

export default function WidgetManager({ layout = 'grid' }) {
  const [widgets, setWidgets] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [veterinarians, setVeterinarians] = useState([]);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [farriers, setFarriers] = useState([]);
  const [showFarrierDropdown, setShowFarrierDropdown] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    loadWidgets();
    fetchVeterinarians();
    fetchFarriers();
  }, []);

  const fetchVeterinarians = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/veterinarians'));

      if (response.data) {
        setVeterinarians(response.data);
      }
    } catch (error) {
      console.error('Error fetching veterinarians:', error);
    }
  };

  const fetchFarriers = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/farriers'));

      if (response.data) {
        setFarriers(response.data);
      }
    } catch (error) {
      console.error('Error fetching farriers:', error);
    }
  };

  const loadWidgets = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl('/api/widgets/layout'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.layout) {
        setWidgets(response.data.layout);
      } else {
        // Default widgets
        const defaultWidgets = [
          {
            id: 'upcomingEvents',
            config: WIDGET_REGISTRY.upcomingEvents.defaultConfig,
            position: 0
          },
          {
            id: 'yearToDateStats',
            config: WIDGET_REGISTRY.yearToDateStats.defaultConfig,
            position: 1
          },
          {
            id: 'recentEntries',
            config: WIDGET_REGISTRY.recentEntries.defaultConfig,
            position: 2
          },
          {
            id: 'membership',
            config: WIDGET_REGISTRY.membership.defaultConfig,
            position: 3
          }
        ];
        setWidgets(defaultWidgets);
        await saveWidgets(defaultWidgets);
      }
    } catch (error) {
      console.error('Error loading widgets:', error);
      // Fallback to default widgets if API fails
      const defaultWidgets = [
        {
          id: 'upcomingEvents',
          config: WIDGET_REGISTRY.upcomingEvents.defaultConfig,
          position: 0
        },
        {
          id: 'yearToDateStats',
          config: WIDGET_REGISTRY.yearToDateStats.defaultConfig,
          position: 1
        },
        {
          id: 'recentEntries',
          config: WIDGET_REGISTRY.recentEntries.defaultConfig,
          position: 2
        },
        {
          id: 'membership',
          config: WIDGET_REGISTRY.membership.defaultConfig,
          position: 3
        }
      ];
      setWidgets(defaultWidgets);
    }
  };

  const saveWidgets = async (widgetList) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(getApiUrl('/api/widgets/layout'), 
        { layout: widgetList },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving widgets:', error);
      // Fallback to localStorage if API fails
      try {
        localStorage.setItem('dashboard-widgets', JSON.stringify(widgetList));
      } catch (localError) {
        console.error('Error saving to localStorage fallback:', localError);
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Ensure we have valid source and destination
    if (result.source.index === result.destination.index) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions
    const updatedItems = items.map((item, index) => ({
      ...item,
      position: index
    }));

    setWidgets(updatedItems);
    await saveWidgets(updatedItems);
  };

  const handleEditModeToggle = async () => {
    if (isEditMode) {
      // Save layout when exiting edit mode
      await saveWidgets(widgets);
    }
    setIsEditMode(!isEditMode);
    
    // Force a small delay to ensure state is updated before any drag operations
    if (!isEditMode) {
      setTimeout(() => {
        // This ensures the drag handles are properly rendered
      }, 100);
    }
  };

  const addWidget = async (widgetId) => {
    const widgetDef = WIDGET_REGISTRY[widgetId];
    if (!widgetDef) return;

    // Check if widget already exists
    if (widgets.some(w => w.id === widgetId)) {
      console.warn(`Widget ${widgetId} already exists`);
      return;
    }

    const newWidget = {
      id: widgetId,
      config: { ...widgetDef.defaultConfig },
      position: widgets.length
    };

    const updatedWidgets = [...widgets, newWidget];
    setWidgets(updatedWidgets);
    await saveWidgets(updatedWidgets);
    setShowAddWidget(false);
  };

  const removeWidget = async (widgetId) => {
    const updatedWidgets = widgets.filter(w => w.id !== widgetId);
    setWidgets(updatedWidgets);
    await saveWidgets(updatedWidgets);
  };

  const updateWidgetConfig = (widgetId, newConfig) => {
    const updatedWidgets = widgets.map(w => 
      w.id === widgetId ? { ...w, config: newConfig } : w
    );
    setWidgets(updatedWidgets);
    
    // Also update the editingWidget state to maintain input focus
    if (editingWidget && editingWidget.id === widgetId) {
      setEditingWidget({ ...editingWidget, config: newConfig });
    }
    
    // Don't save to localStorage immediately - wait for user to save or cancel
    // Don't close the dialog - let user make multiple changes
    setHasUnsavedChanges(true);
  };

  const renderWidget = (widget, index) => {
    const widgetDef = WIDGET_REGISTRY[widget.id];
    if (!widgetDef) return null;

    const WidgetComponent = widgetDef.component;
    return (
      <Draggable key={widget.id} draggableId={widget.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`relative group ${snapshot.isDragging ? 'shadow-2xl rotate-2' : ''}`}
            data-testid={`widget-${widget.id}`}
            data-widget-id={widget.id}
          >
            <WidgetComponent 
              config={widget.config} 
              onConfigChange={(newConfig) => updateWidgetConfig(widget.id, newConfig)}
              layout={layout}
            />
            
            {/* Widget Controls - Always visible */}
            <div className="absolute top-2 right-2 opacity-100">
              <div className="flex gap-1">
                {/* Drag Handle - Always present but only visible in edit mode */}
                <div
                  {...provided.dragHandleProps}
                  className={`p-1 rounded shadow-sm border border-gray-200 transition-all ${
                    isEditMode 
                      ? 'bg-white hover:bg-gray-50 cursor-grab active:cursor-grabbing opacity-100' 
                      : 'bg-transparent cursor-default opacity-0 pointer-events-none'
                  }`}
                  title={isEditMode ? "Drag to reorder" : ""}
                  style={{ touchAction: 'none' }}
                  data-testid="drag-handle"
                  aria-label="Drag handle"
                  data-widget-id={widget.id}
                >
                  <Move className="h-4 w-4 text-gray-600" />
                </div>
                
                <button
                  onClick={() => {
                    setEditingWidget(widget);
                    setHasUnsavedChanges(false);
                  }}
                  className="p-1 bg-white rounded shadow-sm hover:bg-gray-50"
                  title="Configure widget"
                >
                  <Edit3 className="h-4 w-4 text-gray-600" />
                </button>
                
                <button
                  onClick={() => removeWidget(widget.id)}
                  className="p-1 bg-white rounded shadow-sm hover:bg-gray-50"
                  title="Remove widget"
                >
                  <X className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  const renderWidgetSettings = (widget) => {
    const widgetDef = WIDGET_REGISTRY[widget.id];
    if (!widgetDef) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">
              Configure {widgetDef.name}
              {hasUnsavedChanges && <span className="text-sm text-orange-600 ml-2">(Unsaved Changes)</span>}
            </h3>
            <button
              onClick={() => {
                if (hasUnsavedChanges) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to close without saving?')) {
                    setEditingWidget(null);
                    setHasUnsavedChanges(false);
                  }
                } else {
                  setEditingWidget(null);
                }
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Widget Title
              </label>
              <input
                type="text"
                value={widget.config.title}
                onChange={(e) => updateWidgetConfig(widget.id, {
                  ...widget.config,
                  title: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Widget-specific settings */}
            {widget.id === 'upcomingEvents' && (
              <>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Events
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={widget.config.maxEvents}
                    onChange={(e) => updateWidgetConfig(widget.id, {
                      ...widget.config,
                      maxEvents: parseInt(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Days Ahead
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={widget.config.daysAhead}
                    onChange={(e) => updateWidgetConfig(widget.id, {
                      ...widget.config,
                      daysAhead: parseInt(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={widget.config.showLocation}
                        onChange={(e) => updateWidgetConfig(widget.id, {
                          ...widget.config,
                          showLocation: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show Location</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={widget.config.showTime}
                        onChange={(e) => updateWidgetConfig(widget.id, {
                          ...widget.config,
                          showTime: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-medium text-gray-700">Show Time</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            {widget.id === 'recentEntries' && (
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Entries
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={widget.config.maxEntries}
                  onChange={(e) => updateWidgetConfig(widget.id, {
                    ...widget.config,
                    maxEntries: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

             {widget.id === 'yearToDateStats' && (
               <>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Start Date
                   </label>
                   <input
                     type="date"
                     value={widget.config.startDate}
                     onChange={(e) => updateWidgetConfig(widget.id, {
                       ...widget.config,
                       startDate: e.target.value
                     })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     End Date
                   </label>
                   <input
                     type="date"
                     value={widget.config.endDate}
                     onChange={(e) => updateWidgetConfig(widget.id, {
                       ...widget.config,
                       endDate: e.target.value
                     })}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                   />
                 </div>
                 <div className="lg:col-span-2">
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <label className="flex items-center">
                       <input
                         type="checkbox"
                         checked={widget.config.showPoints}
                         onChange={(e) => updateWidgetConfig(widget.id, {
                           ...widget.config,
                           showPoints: e.target.checked
                         })}
                         className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                       />
                       <span className="ml-2 text-sm text-gray-700">Show Points</span>
                     </label>
                     <label className="flex items-center">
                       <input
                         type="checkbox"
                         checked={widget.config.showEarnings}
                         onChange={(e) => updateWidgetConfig(widget.id, {
                           ...widget.config,
                           showEarnings: e.target.checked
                         })}
                         className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                       />
                       <span className="ml-2 text-sm text-gray-700">Show Earnings</span>
                     </label>
                   </div>
                 </div>
                 <div className="lg:col-span-2">
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <label className="flex items-center">
                       <input
                         type="checkbox"
                         checked={widget.config.showDateRange}
                         onChange={(e) => updateWidgetConfig(widget.id, {
                           ...widget.config,
                           showDateRange: e.target.checked
                         })}
                         className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                       />
                       <span className="ml-2 text-sm text-gray-700">Show Date Range</span>
                     </label>
                     <label className="flex items-center">
                       <input
                         type="checkbox"
                         checked={widget.config.showTrend}
                         onChange={(e) => updateWidgetConfig(widget.id, {
                           ...widget.config,
                           showTrend: e.target.checked
                         })}
                         className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                       />
                       <span className="ml-2 text-sm text-gray-700">Show Trend</span>
                     </label>
                   </div>
                 </div>
               </>
             )}

                           {widget.id === 'veterinarian' && (
                <>
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selected Veterinarian
                    </label>
                    <div className="relative">
                      <button
                        onClick={() => setShowVetDropdown(!showVetDropdown)}
                        className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 font-medium">V</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-gray-900">
                              {veterinarians.find(vet => vet.id === widget.config.selectedVetId)?.name || 'Select Veterinarian'}
                            </div>
                            {veterinarians.find(vet => vet.id === widget.config.selectedVetId)?.clinic_name && (
                              <div className="text-sm text-gray-600">
                                {veterinarians.find(vet => vet.id === widget.config.selectedVetId)?.clinic_name}
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showVetDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown */}
                      {showVetDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                          {veterinarians.map((vet) => (
                            <button
                              key={vet.id}
                              onClick={() => {
                                updateWidgetConfig(widget.id, {
                                  ...widget.config,
                                  selectedVetId: vet.id
                                });
                                setShowVetDropdown(false);
                              }}
                              className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                                widget.config.selectedVetId === vet.id ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                              }`}
                            >
                              <div className="font-medium text-gray-900">{vet.name}</div>
                              {vet.clinic_name && (
                                <div className="text-sm text-gray-600">{vet.clinic_name}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showClinic}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showClinic: e.target.checked
                          })}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show Clinic Name</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showEmail}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showEmail: e.target.checked
                          })}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show Email</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {widget.id === 'farrier' && (
                <>
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selected Farrier
                    </label>
                    <div className="relative">
                      <button
                        onClick={() => setShowFarrierDropdown(!showFarrierDropdown)}
                        className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium">F</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-gray-900">
                              {farriers.find(farrier => farrier.id === widget.config.selectedFarrierId)?.name || 'Select Farrier'}
                            </div>
                            {farriers.find(farrier => farrier.id === widget.config.selectedFarrierId)?.clinic_name && (
                              <div className="text-sm text-gray-600">
                                {farriers.find(farrier => farrier.id === widget.config.selectedFarrierId)?.clinic_name}
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showFarrierDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown */}
                      {showFarrierDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                          {farriers.map((farrier) => (
                            <button
                              key={farrier.id}
                              onClick={() => {
                                updateWidgetConfig(widget.id, {
                                  ...widget.config,
                                  selectedFarrierId: farrier.id
                                });
                                setShowFarrierDropdown(false);
                              }}
                              className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                                widget.config.selectedFarrierId === farrier.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                              }`}
                            >
                              <div className="font-medium text-gray-900">{farrier.name}</div>
                              {farrier.clinic_name && (
                                <div className="text-sm text-gray-600">{farrier.clinic_name}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showClinic}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showClinic: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show Clinic Name</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showEmail}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showEmail: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show Email</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {widget.id === 'membership' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Memberships
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={widget.config.maxMemberships}
                      onChange={(e) => updateWidgetConfig(widget.id, {
                        ...widget.config,
                        maxMemberships: parseInt(e.target.value)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showExpiringSoon}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showExpiringSoon: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">Show Expiring Soon</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showExpired}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showExpired: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">Show Expired</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={widget.config.showActive}
                          onChange={(e) => updateWidgetConfig(widget.id, {
                            ...widget.config,
                            showActive: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">Show Active</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
              
              {/* Save Button */}
              <div className="lg:col-span-2 pt-4 border-t border-gray-200">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        if (window.confirm('You have unsaved changes. Are you sure you want to cancel without saving?')) {
                          setEditingWidget(null);
                          setHasUnsavedChanges(false);
                        }
                      } else {
                        setEditingWidget(null);
                      }
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      // Save the current widget configurations to API
                      await saveWidgets(widgets);
                      // Clear the unsaved changes flag
                      setHasUnsavedChanges(false);
                      // Close the dialog
                      setEditingWidget(null);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddWidgetModal = () => {
    const availableWidgets = Object.values(WIDGET_REGISTRY).filter(
      widgetDef => !widgets.find(w => w.id === widgetDef.id)
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Add Widget</h3>
            <button
              onClick={() => setShowAddWidget(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableWidgets.map((widgetDef) => (
              <div
                key={widgetDef.id}
                onClick={() => addWidget(widgetDef.id)}
                className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-medium">{widgetDef.icon[0]}</span>
                  </div>
                  <h4 className="font-medium text-gray-900">{widgetDef.name}</h4>
                </div>
                <p className="text-sm text-gray-600">{widgetDef.description}</p>
              </div>
            ))}
          </div>
          
          {availableWidgets.length === 0 && (
            <p className="text-center text-gray-500 py-8">
              All available widgets are already added to your dashboard.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <HomeIcon className="w-8 h-8 text-black" />
          Home
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleEditModeToggle}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-colors ${
              isEditMode 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <Edit3 className="h-4 w-4" />
            <span className="hidden sm:inline">
              {isEditMode ? 'Exit Edit Mode' : 'Edit Layout'}
            </span>
          </button>
          <button
            onClick={() => setShowAddWidget(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#b97a3a] text-white rounded-md hover:bg-[#c98540]"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Widget</span>
          </button>
        </div>
      </div>

      {/* Edit Mode Instructions */}
      {isEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Edit3 className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900 mb-1">Edit Mode Active</h3>
              <p className="text-sm text-blue-700">
                Drag the <Move className="inline h-3 w-3" /> handle to reorder widgets. 
                Use the <Edit3 className="inline h-3 w-3" /> button to configure each widget. 
                Click "Exit Edit Mode" when you're done.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Widget Grid */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="widgets" direction="horizontal">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`grid gap-3 sm:gap-4 ${
                layout === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                  : layout === 'list'
                  ? 'grid-cols-1'
                  : 'grid-cols-1 gap-2' // compact layout
              } ${isEditMode ? 'p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300' : ''}`}
            >
              {widgets.map((widget, index) => renderWidget(widget, index))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Empty state */}
      {widgets.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Grid className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No widgets yet</h3>
          <p className="text-gray-500 mb-4">Add some widgets to customize your dashboard</p>
          <button
            onClick={() => setShowAddWidget(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#b97a3a] text-white rounded-md hover:bg-[#c98540] mx-auto"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Your First Widget</span>
          </button>
        </div>
      )}

      {/* Modals */}
      {editingWidget && renderWidgetSettings(editingWidget)}
      {showAddWidget && renderAddWidgetModal()}
    </div>
  );
} 